import { createContext, useEffect } from "react";
import {
	BrowserRouter,
	Route,
	Routes,
	Navigate,
	useLocation,
	useNavigate,
	useSearchParams,
} from "react-router-dom";
import { PATHS } from "./routes.paths";
import API from "../services/utils/api"
import packageJson from "../../package.json";
import { useGetCompaniesByOrganizationId } from "../services/organization.service";
import {
	adminRoutes,
	authRoutes,
	hrRoutes,
	payrollCompanyRoutes,
	payrollCompanySetupRoutes,
	selfServiceRoutes
} from "./routes.data";
import useUserStore from "../state-management/useUserStore";
import useCompanyStore from "../state-management/useCompanyStore";
import { useIdleTimer } from "../hooks/useIdleTimer";
import SessionTimeoutModal from "../components/modals/SessionTimeoutModal";
import { useSupervisorCheck } from "../pages/self_service/dashboard/hooks/useSupervisorCheck";
import { EmployeeDto } from "../models/employee.dto";
import { useAsyncEffect, useLockFn } from "ahooks";
import { SubscriptionTierOrderBy } from "../models/subscription-tier.dto";
import ErrorPage, { ErrorPageType } from "../pages/shared/error";

export const LoadingCompanies = createContext<boolean>(false);

function AppRoutes() {
	return (
		<BrowserRouter basename={packageJson.homepage || "/"}>
			<Routes>
				{/* Authentication */}
				{authRoutes.map(({ path, element: Component }) => (
					<Route
						key={path}
						path={path}
						element={
							<NoAuthRequired>
								<Component />
							</NoAuthRequired>
						}
					/>
				))}

				{/* Admin Dashboard */}
				{adminRoutes.map(({ path, element: Component }) => (
					<Route
						key={path}
						path={path}
						element={
							<RequireAuth>
								<AdminRoutes>
									<Component errorType={ErrorPageType.FORBIDDEN} />
								</AdminRoutes>
							</RequireAuth>
						}
					/>
				))}

				{/* Payroll Company */}
				{payrollCompanySetupRoutes.map(({ path, element: Component }) => (
					<Route
						key={path}
						path={path}
						element={
							<RequireAuth>
								<PayrollCompanyRoutes>
									<Component />
								</PayrollCompanyRoutes>
							</RequireAuth>
						}
					/>
				))}
				
				{payrollCompanyRoutes.map(({ path, element: Component }) => (
					<Route
						key={path}
						path={path}
						element={
							<RequireAuth>
								<PayrollCompanyRoutes>
									<Component errorType={ErrorPageType.FORBIDDEN} />
								</PayrollCompanyRoutes>
							</RequireAuth>
						}
					/>
				))}

				{/* Human Resource */}
				{hrRoutes.map(({ path, element: Component }) => (
					<Route
						key={path}
						path={path}
						element={
							<RequireAuth>
								<HumanResourceRoutes>
									<Component errorType={ErrorPageType.FORBIDDEN} />
								</HumanResourceRoutes>
							</RequireAuth>
						}
					/>
				))}

				{/* Self Service Portal */}
				{selfServiceRoutes.map(({ path, element: Component }) => (
					<Route
						key={path}
						path={path}
						element={
							<RequireAuth>
								<SelfServiceRoutes>
									<Component errorType={ErrorPageType.FORBIDDEN} />
								</SelfServiceRoutes>
							</RequireAuth>
						}
					/>
				))}

				<Route 
					key={"notFound"} 
					path={"*"} 
					element={<ErrorPage errorType={ErrorPageType.NOT_FOUND} />}
				/>
			</Routes>
		</BrowserRouter>
	);
}

// For routes that do not require authentication. Re-routes the user back to the app if signed in.
function NoAuthRequired({ children }: { children: JSX.Element }) {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { currentUser, userType } = useUserStore();
	const location = useLocation();
	const PrivateRoutes = adminRoutes.concat(
		adminRoutes,
		payrollCompanySetupRoutes, 
		payrollCompanyRoutes, 
		hrRoutes, 
		selfServiceRoutes,
	);

	const isAPrivateRoute = PrivateRoutes.map((x) =>
		x.path.toLowerCase()
	).includes(location.pathname.toLowerCase());
	const userSignedIn = currentUser?.user?.id;

	useEffect(() => {
		const prevPage = decodeURIComponent(
			searchParams.get("prev_page") || ""
		);

		if (userSignedIn && !isAPrivateRoute) {
			switch(userType) {
				case "ADMIN":
					if (prevPage && prevPage.startsWith("/admin")) {
						navigate(prevPage);
						return
					}
					navigate(PATHS.ADMIN_ACCOUNTS_MANAGEMENT);
					break;

				case "ORGANIZATION":
					if (prevPage && prevPage.startsWith("/dashboard")) {
						navigate(prevPage);
						return
					}
					navigate(PATHS.OVERVIEW);
					break;

				case "HR":
					if (
						prevPage && (prevPage.startsWith("/hr") || 
						prevPage.startsWith("/selfservice"))
					) {
						navigate(prevPage);
						return
					}
					navigate(PATHS.HR_EMPLOYEE_MANAGEMENT);
					break;

				case "EMPLOYEE":
					if (prevPage && prevPage.startsWith("/selfservice")) {
						navigate(prevPage);
						return;
					}
					navigate(PATHS.SELF_SERVICE_DASHBOARD);
					break;
			}
		}
	}, [userSignedIn, userType])

	return children;
}

// For routes that require authentication. Re-routes the user to the sign in page if not signed in.
function RequireAuth({ children }: { children: JSX.Element }) {
	const location = useLocation();
	const { currentUser } = useUserStore();
	const { showWarning, secondsRemaining, continueSession } = useIdleTimer();

	if (!currentUser?.user?.id) {
		return <Navigate
			to={{
				pathname: PATHS.SIGN_IN,
				search: `?prev_page=${encodeURIComponent(location.pathname + location.search)}`
			}}
		/>
	}

	return <>
		{children}
		<SessionTimeoutModal
			open={showWarning}
			secondsRemaining={secondsRemaining}
			onContinue={continueSession}
		/>
	</>;
}

// Pages a user without a company is meant to be on while setting one up
const COMPANY_SETUP_PATHS: string[] = [
	PATHS.CREATE_PAYROLL_COMPANY,
	PATHS.COMPANY_ACCOUNT_SETUP,
	PATHS.SUBSCRIPTION_PAYMENT,
];

function PayrollCompanyRoutes({ children }: { children: JSX.Element }) {
	const navigate = useNavigate();
	const location = useLocation();
	const { currentUser, userType } = useUserStore();
	const { 
		currentCompany, 
		savedCompanies,
		setCurrentCompany,
		setSavedCompanies,
	} = useCompanyStore();
	const recentCompanyId = localStorage.getItem("recentCompanyId");

	const { 
		data: companies, 
		isLoading: isLoadingCompanies,
		error 
	} = useGetCompaniesByOrganizationId(
		currentUser.user.organizationId, 
		true,
	);

	// Set the current company to the first company in the list if there is no recent company.
	// Else, set the current company to the recent company (active company on the user's previous session)
	useEffect(() => {
		if (companies?.data && !savedCompanies.length) {
			setSavedCompanies(companies.data || []);
			if (recentCompanyId) {
				setCurrentCompany(
					companies.data.find(company => company.id === Number(recentCompanyId)) || 
					companies.data[0] as any
				);
				return
			}
			setCurrentCompany(companies.data[0] as any);
		}
	}, [isLoadingCompanies]);

	// Redirect to the create payroll company page if the user has no company.
	// The companies query refetches when the window regains focus (e.g. after closing
	// a file picker or a checkout tab), so never redirect away from the setup flow
	// itself, or the user loses what they've filled in.
	useEffect(() => {
		if (COMPANY_SETUP_PATHS.includes(location.pathname)) return;

		if (
			!currentCompany &&
			((error as any)?.response?.data?.error === "FORBIDDEN" ||
			(error as any)?.response?.status === 403) ||
			currentUser.user.companyProfiles?.length === 0
		) {
			navigate(PATHS.CREATE_PAYROLL_COMPANY);
		}
	}, [isLoadingCompanies, error]);

	// Redirect to the forbidden page if the user type is not an organization
	useEffect(() => {
		if (!userType) return;

		if (userType !== "ORGANIZATION") {
			navigate(PATHS.PC_FORBIDDEN);
		} 
	}, [userType])

	// Get the highest subscription tier id and store it in the session storage
    useAsyncEffect(useLockFn(async () => {
        if (sessionStorage.getItem("highestSubscriptionTierId")) return;

        try {
            const { data } = await API.BillingAndSubscriptionAPI.getSubscriptionTierPackages({
				page: 1,
				limit: 20,
				orderBy: SubscriptionTierOrderBy.PRICE_AMOUNT_ASC,
			});

            sessionStorage.setItem("highestSubscriptionTierId", data[data.length - 1].id);
        } catch {}
    }), [])

	return (
		<LoadingCompanies.Provider value={isLoadingCompanies}>
			{children}
		</LoadingCompanies.Provider>
	)
}

function AdminRoutes({ children }: { children: JSX.Element }) {
	const { userType } = useUserStore();
	const navigate = useNavigate();

	useEffect(() => {
		if (!userType) return;

		if (userType !== "ADMIN") {
			navigate(PATHS.ADMIN_FORBIDDEN);
		} 
	}, [userType])

	return children
}

function HumanResourceRoutes({ children }: { children: JSX.Element }) {
	const navigate = useNavigate();
    const { currentUser, userType } = useUserStore();
	const { currentCompany, setCurrentCompany } = useCompanyStore();
	const companyProfile = (currentUser.user.companyProfiles || [])[0];

	useEffect(() => {
		if (!userType) return;

		if (userType !== "HR") {
			navigate(PATHS.HR_FORBIDDEN);
		} 
	}, [userType])

    useAsyncEffect(useLockFn(async () => {
        if (currentCompany) return;

        try {
            const response = await API.ProcessingCompanyAPI.getCompanyById(companyProfile?.companyId);

            setCurrentCompany(response.data);
        } catch {}
    }), [companyProfile?.companyId])

	return children
}

export const EmployeeSupervisees = createContext<EmployeeDto[]>([]);

function SelfServiceRoutes({ children }: { children: JSX.Element }) {
	const navigate = useNavigate();
    const { currentUser, userType } = useUserStore();
	const companyProfile = (currentUser.user.companyProfiles || [])[0];

	useEffect(() => {
		if (!userType) return;

		if (userType === "ORGANIZATION" || userType === "ADMIN") {
			navigate(PATHS.SELF_SERVICE_FORBIDDEN);
		} 
	}, [userType])
	
	// Get the supervisees of the current user if the user is a supervisor
	const { supervisees } = useSupervisorCheck(companyProfile?.companyId);

	return <EmployeeSupervisees.Provider value={supervisees}> 
		{children}
	</EmployeeSupervisees.Provider>
}

export default AppRoutes;
